import { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useSelector } from "react-redux";
import { getLocationHierarchy } from "../../global/utility/widgetUtils";
import "./charts.css";
import { toMin } from "../../global/utility/statisticsUtil";
import { xAXIS_TOOLTIP_UNIT_IN_MINUTE, xAXIS_TOOLTIP_UNIT_IN_PERCENT } from "./statisticsConstants";
import {
  CHART_FONT_FAMILY,
  TOOLTIP_STYLE,
  chartTextStyle,
  buildChartTitle,
  buildLegend,
  buildSliderDataZoom,
  SLIDER_LABEL_EVENTS,
  CHART_GRID_BOTTOM,
  buildYAxisNameGap,
  formatAxisValue,
  createChartToolbox,
  CHART_GRID_RIGHT_PADDING,
} from "./ApacheChartUtils";

const INVISIBLE_PAD = "\u200B";
const displayName = (name) => String(name ?? "").split(INVISIBLE_PAD).join("");

const ApacheLineChart = ({
  title = "",
  xAxisData = [],
  tooltipXAxisData = [],
  series = [],
  loading = false,
  showLegend = false,
  xisData = {},
}) => {
  const chartRef = useRef(null);
  const siteTagList = useSelector((state) => state.sitesReducer.siteTagList);
  const option = useMemo(() => {
    // ECharts keys legend entries (and their toggle state) by series name, so locations that
    // share a name would collapse into one legend item. Pad repeats with invisible characters to
    // keep one legend entry per location; they are stripped again wherever the name is displayed.
    const seenNames = new Map();
    const uniqueNames = series.map((s) => {
      const count = seenNames.get(s?.name) ?? 0;
      seenNames.set(s?.name, count + 1);
      return count === 0 ? s?.name : `${s?.name}${INVISIBLE_PAD.repeat(count)}`;
    });
    const hierarchyBySeriesName = new Map();
    series.forEach((s, i) => {
      const hierarchy = s?.locationId && getLocationHierarchy(siteTagList, s.locationId);
      if (hierarchy) hierarchyBySeriesName.set(uniqueNames[i], hierarchy);
    });
    const maxVal = series.reduce((max, s) => {
      const vals = (s?.data ?? []).filter((v) => v != null);
      return vals.length > 0 ? Math.max(max, Math.max(...vals)) : max;
    }, 0);
    const yAxisNameGap = Math.min(buildYAxisNameGap(maxVal, xisData?.toolTipUnit), 80);

    return {
      textStyle: chartTextStyle,
      title: buildChartTitle(title),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "none" },
        textStyle: { ...chartTextStyle, color: "#000000" },
        formatter: (params) => {
          const idx = params[0]?.dataIndex;
          const xData = xAxisData?.data ?? xAxisData;
          const rawLabel = tooltipXAxisData[idx];
          const label = rawLabel ?? (xData?.[idx] ?? params[0]?.axisValue);
          const unit = xAxisData?.toolTipUnit ?? "";
          const isMinutes = xisData?.toolTipUnit === xAXIS_TOOLTIP_UNIT_IN_MINUTE;
          const valueSuffix = unit === xAXIS_TOOLTIP_UNIT_IN_PERCENT ? "%" : "";
          const formatValue = (val) => isMinutes ? toMin(val) : `${val}${valueSuffix}`;
          const rows = params
            .map((p) => `<div style="${TOOLTIP_STYLE}">${p.marker}${displayName(p.seriesName)}: <b>${formatValue(p.value)}</b></div>`)
            .join("");
          const additionalRow =
            unit === "Occupancy" && xAxisData?.additionalToolTip != null
              ? `<div style="${TOOLTIP_STYLE}">Total Capacity: <b>${xAxisData.additionalToolTip}</b></div>`
              : "";
          return `<div style="${TOOLTIP_STYLE}">${label}</div>${rows}${additionalRow}`;
        },
      },
      legend: {
        ...buildLegend(showLegend),
        formatter: displayName,
        tooltip: {
          show: hierarchyBySeriesName.size > 0,
          textStyle: { ...chartTextStyle, color: "#000000" },
          formatter: (params) =>
            `<div style="${TOOLTIP_STYLE}">${hierarchyBySeriesName.get(params?.name) ?? displayName(params?.name)}</div>`,
        },
      },
      toolbox: createChartToolbox(chartRef),
      grid: {
        left: "5%",
        right: CHART_GRID_RIGHT_PADDING,
        bottom: CHART_GRID_BOTTOM,
        top: title ? 60 : 40,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: xAxisData?.data ?? xAxisData,
        name: xisData?.xName || xAxisData?.xName || "Time",
        nameLocation: "middle",
        nameGap: 25,
        nameTextStyle: { fontWeight: "bold" },
        axisLabel: { rotate: 0, interval: "auto", fontFamily: CHART_FONT_FAMILY, fontSize: 12 },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        name: xisData?.yName || xAxisData?.yName || "",
        nameLocation: "middle",
        nameGap: yAxisNameGap,
        nameRotate: 90,
        nameTextStyle: { fontWeight: "bold" },
        splitLine: { show: true },
        axisLabel: {
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          formatter: (val) => formatAxisValue(val, xisData?.toolTipUnit),
        },
      },
      dataZoom: buildSliderDataZoom(0, "x"),
      series: series.map((s, i) => ({
        name: uniqueNames[i],
        type: "line",
        smooth: s?.smooth ?? false,
        areaStyle: s?.areaStyle ?? null,
        data: s?.data,
        symbol: "none",
        color: s?.color ?? "#0089EC",
        lineStyle: { width: 3 },
        itemStyle: { borderWidth: 0 },
        emphasis: { focus: "series" },
        markPoint: xAxisData?.markerPoint ? {
        data: [{  value: s?.data[10], xAxis: 10, yAxis: s?.data[10] }]
      } : {},
        label: {
          show: s?.showLabel ?? false,
          position: "top",
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          color: "#000000",
        },
      })),
    };
  }, [title, xAxisData, tooltipXAxisData, series, xisData, siteTagList]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height: undefined }}
      className="echartStyles"
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={SLIDER_LABEL_EVENTS}
    />
  );
};

export default ApacheLineChart;
